import React, {useState, useEffect, useCallback} from "react";
import {Button, Alert} from "antd";
import {CloseOutlined, MinusOutlined, PlusOutlined, LinkOutlined} from "@ant-design/icons";
import css from "./AnnouncementBanner.less";
import {useGetActiveAnnouncementsQuery} from "../../generated/graphql";

type Announcement = {
    id: string;
    title: string;
    message: string;
    type: "Info" | "Warning" | "Error" | "Success";
    priority: "Low" | "Medium" | "High";
    isDismissible: boolean;
    isMinimizable: boolean;
    targetUrl?: string;
    targetUrlLabel?: string;
};

const STORAGE_KEY_DISMISSED = "dismissedAnnouncements";
const STORAGE_KEY_MINIMIZED = "minimizedAnnouncements";

const AnnouncementBanner: React.FC = () => {
    const [dismissedIds, setDismissedIds] = useState<Array<string>>([]);
    const [minimizedIds, setMinimizedIds] = useState<Array<string>>([]);
    const [showMinimizedBar, setShowMinimizedBar] = useState(false);

    const {data, loading, error, stopPolling} = useGetActiveAnnouncementsQuery({
        // pollInterval: 60000, // Poll every minute for new announcements
        pollInterval: 300000, // Poll every 5 minutes for new announcements
        fetchPolicy: "network-only",
    });

    // Stop polling on authentication errors to prevent log flooding
    useEffect(() => {
        if (error) {
            stopPolling();
        }
    }, [error, stopPolling]);

    // Load dismissed and minimized IDs from localStorage on mount
    useEffect(() => {
        try {
            const dismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);
            const minimized = localStorage.getItem(STORAGE_KEY_MINIMIZED);

            if (dismissed) {
                setDismissedIds(JSON.parse(dismissed));
            }

            if (minimized) {
                const parsedMinimized = JSON.parse(minimized);

                setMinimizedIds(parsedMinimized);
                setShowMinimizedBar(parsedMinimized.length > 0);
            }
        } catch (error) {
            console.error("Error loading announcement state from localStorage:", error);
        }
    }, []);

    const handleDismiss = useCallback((id: string) => {
        setDismissedIds((prev) => {
            const newDismissed = [...prev, id];

            localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(newDismissed));

            return newDismissed;
        });
    }, []);

    const handleMinimize = useCallback((id: string) => {
        setMinimizedIds((prev) => {
            const newMinimized = [...prev, id];

            localStorage.setItem(STORAGE_KEY_MINIMIZED, JSON.stringify(newMinimized));
            setShowMinimizedBar(true);

            return newMinimized;
        });
    }, []);

    const handleMaximize = useCallback((id: string) => {
        setMinimizedIds((prev) => {
            const newMinimized = prev.filter((minimizedId) => minimizedId !== id);

            localStorage.setItem(STORAGE_KEY_MINIMIZED, JSON.stringify(newMinimized));

            if (newMinimized.length === 0) {
                setShowMinimizedBar(false);
            }

            return newMinimized;
        });
    }, []);

    if (loading || !data?.activeAnnouncements) {
        return null;
    }

    const announcements: Array<Announcement> = data.activeAnnouncements as Array<Announcement>;

    // Filter out dismissed announcements
    const activeAnnouncements = announcements.filter((announcement) => !dismissedIds.includes(announcement.id));

    // Separate minimized and visible announcements
    const visibleAnnouncements = activeAnnouncements.filter((announcement) => !minimizedIds.includes(announcement.id));
    const minimizedAnnouncements = activeAnnouncements.filter((announcement) => minimizedIds.includes(announcement.id));

    const getAlertType = (type: Announcement["type"]) => {
        switch (type) {
            case "Info":
                return "info";
            case "Warning":
                return "warning";
            case "Error":
                return "error";
            case "Success":
                return "success";
            default:
                return "info";
        }
    };

    return (
        <>
            {/* Visible Announcements */}
            {visibleAnnouncements.length > 0 && (
                <div className={css.announcementContainer}>
                    {visibleAnnouncements.map((announcement) => (
                        <Alert
                            key={announcement.id}
                            message={announcement.title}
                            description={
                                <div>
                                    <p>{announcement.message}</p>
                                    {announcement.targetUrl && (
                                        <a href={announcement.targetUrl} target="_blank" rel="noopener noreferrer" className={css.announcementLink}>
                                            <LinkOutlined /> {announcement.targetUrlLabel ?? "Mehr erfahren"}
                                        </a>
                                    )}
                                </div>
                            }
                            type={getAlertType(announcement.type)}
                            showIcon
                            className={css.announcementAlert}
                            action={
                                <div className={css.announcementActions}>
                                    {announcement.isMinimizable && <Button size="small" type="text" icon={<MinusOutlined />} onClick={() => handleMinimize(announcement.id)} />}
                                    {announcement.isDismissible && <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => handleDismiss(announcement.id)} />}
                                </div>
                            }
                        />
                    ))}
                </div>
            )}

            {/* Minimized Bar */}
            {showMinimizedBar && minimizedAnnouncements.length > 0 && (
                <div className={css.minimizedBar}>
                    <span className={css.minimizedText}>
                        {minimizedAnnouncements.length} {minimizedAnnouncements.length === 1 ? "minimierte Meldung" : "minimierte Meldungen"}
                    </span>
                    <div className={css.minimizedButtons}>
                        {minimizedAnnouncements.map((announcement) => (
                            <Button key={announcement.id} size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleMaximize(announcement.id)} className={css.minimizedButton}>
                                {announcement.title}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default AnnouncementBanner;
